/** ========================================================================= *\
 * @state Playing State
 * ========================================================================== */
import CountUp from 'countup.js';

import HUD from 'prefabs/hud';
import Doggo from 'prefabs/doggo';
import Markers from 'prefabs/markers';
import Scores from 'prefabs/scores';
import EnemyController from 'prefabs/enemyGroup';
import PowerbarGroup from 'prefabs/PowerbarGroup';
import Background from 'prefabs/background';
import FarBackground from 'prefabs/farBackground';
import { EndHouseBackground, EndHouseForeground } from 'prefabs/endHouse';
import Floor from 'prefabs/floor';
import Sky from 'prefabs/sky';
import * as CONST from 'config';

class Play extends Phaser.State {

  static hasAttachedDOMEvents = false;

  /** Properties ------------------------------------------------------------ */
  entities = {};
  _hasTriggeredWinEvent = false;
  scores = 0;
  obsPoint = 0;
  startTime;

  /** Create world ---------------------------------------------------------- */
  create() {
    const game = this.game;

    // Configure world
    this.game.stage.backgroundColor = "#000000";
    this.game.world.setBounds(0, 0, this.game.width+500, this.game.height);
    // Create event dispatchers
    this.game.onStart = new Phaser.Signal();
    this.game.onDamage = new Phaser.Signal();
    this.game.onAddLives = new Phaser.Signal();
    this.game.onWin = new Phaser.Signal();
    this.game.onLose = new Phaser.Signal();
    this.game.onEnd = new Phaser.Signal();

    // Create entities
    this._createEntities();

    // Setup Camera
    const cameraOffsetX = (this.game.width / 2) - 50;
    this.game.camera.focusOn(this.entities.doggo);
    // this.game.camera.follow(this.entities.doggo, Phaser.Camera.FOLLOW_TOPDOWN_TIGHT, 0.1, 0.1, cameraOffsetX, 0);

    // Attach event listeners
    this.entities.doggo.events.onKilled.add(() => this.loseGame(),this);
    this._attachDOMEvents();
    this.game.onWin.add(() => this.game.onEnd.dispatch());
    this.game.onLose.add(() => this.game.onEnd.dispatch());

    this._hasTriggeredWinEvent = false;

    // Enable Music
    this.music = this.game.add.audio('bgMusic', 0.2, true);
    this.hitSound = this.game.add.sound("ObsHit");
    this.GameStartMusic = this.game.add.sound('GameStart');
    this.GameWinSound = this.game.add.sound("GameWin");
    this.GameOverSound = this.game.add.sound("GameOver");
    this.CoinSound = this.game.add.sound("CoinSound");
    this.game.onStart.add(() => this.music.play());

    // Track analytics
    this.game.onStart.add(() => gtag('event', 'game_start'));
    this.game.onWin.add(() => gtag('event', 'game_win'));
    // this.game.onLose.add(() => gtag('event', 'game_lose', { 'event_label': this.game.progress.toFixed(2) }));

    // Setup scores and damage
    this.scores = 0
    this.game.damage = 1;
    this.game.gameSpeedUp = 0;
    this.game.obsPoint = -10;
    this.game.gameTime = null;
  }

  _createEntities() {
    // Define common variables
    const floorY = this.game.world.height - (CONST.FLOOR_HEIGHT * 0.5) - CONST.MARKER_CONTAINER_HEIGHT;
    const airY = this.game.world.height - (CONST.FLOOR_HEIGHT * 0.5) - CONST.MARKER_CONTAINER_HEIGHT -300;

    // Create sky
    this.entities.sky = new Sky(this.game);
    this.game.world.addChild(this.entities.sky);

    // Create far background
    this.entities.farBackground = new FarBackground(this.game, this.game.world.height - CONST.FLOOR_HEIGHT - CONST.MARKER_CONTAINER_HEIGHT);
    this.game.world.addChild(this.entities.farBackground);

    // Create floor
    this.entities.floor = new Floor(this.game, 0, this.game.world.height - CONST.MARKER_CONTAINER_HEIGHT);
    this.game.world.addChild(this.entities.floor);

    // Create markers
    this.entities.markers = new Markers(this.game, {
      containerPos: new Phaser.Point(0, this.game.height - CONST.MARKER_CONTAINER_HEIGHT),
      containerSize: { w: this.game.width, h: CONST.MARKER_CONTAINER_HEIGHT },
      markerSize: { w: CONST.MARKER_SIZE.W, h: CONST.MARKER_SIZE.H }
    });
    this.game.world.addChild(this.entities.markers);

    // Create doggo
    this.entities.doggo = new Doggo(this.game, {x:250, y: floorY - 100, dogtype:this.game.dogType });
    this.entities.doggo.body.collideWorldBounds=true;
    this.game.world.addChild(this.entities.doggo);

    // Create enemies
    this.entities.enemies = new EnemyController(this.game, floorY,this.entities.doggo);
    this.game.world.addChild(this.entities.enemies);

    // Create Powerbars
    this.entities.powers = new PowerbarGroup(this.game, airY,this.entities.doggo);
    this.game.world.addChild(this.entities.powers);

    // Create Physics Floor
    this.entities.physicsFloor = this.add.tileSprite(0, floorY, this.game.width, 1, "farback");
    this.game.physics.enable(this.entities.physicsFloor, Phaser.Physics.ARCADE);
    this.entities.physicsFloor.body.immovable = true;
    this.entities.physicsFloor.fixedToCamera = true;

    // Create scores
    this.entities.scores = new Scores(this.game);
    this.game.world.addChild(this.entities.scores);

    this.entities.hud = new HUD(this.game, CONST.LIVES);
    this.game.world.addChild(this.entities.hud);

    //Create Custom Bounds
    this.game.physics.arcade.setBounds(0, 0, this.game.width, this.game.height);
  }

  _attachDOMEvents() {
    if (Play.hasAttachedDOMEvents) { return; }

    this.game.paused = true;

    // Enable game start modal buttons
    const gameStartModal = document.getElementById('game-start-modal');
    const gameStartButton = gameStartModal.querySelector('[data-start-button]');
    gameStartButton.addEventListener('click', (e) => {
      this.game.paused = false;
      this.game.onStart.dispatch();
      this.startTime = new Date();
      gameStartModal.classList.remove('is-active');
    });

    // Activate game start modal
    gameStartModal.classList.add('is-active');

    // Enable game win modal buttons
    const gameWinModal = document.getElementById('game-win-modal');
    const gameWinButton = gameWinModal.querySelector('[data-play-again]');
    gameWinButton.addEventListener('click', (e) => {
      this.game.paused = false;
      // this.state.restart();
      this.state.start('PreSelection');
      this.startTime = new Date();
      gameWinModal.classList.remove('is-active');
    });

    // Enable game over modal buttons
    const gameLoseModal = document.getElementById('game-lose-modal');
    const gameLoseButton = gameLoseModal.querySelector('[data-play-again]');
    const gameSubmitButton = gameLoseModal.querySelector('[data-submit]');
    gameLoseButton.addEventListener('click', (e) => {
      this.game.paused = false;
      // this.state.restart();
      this.state.start('PreSelection');
      this.obsPoint = 0;
      this.scores = 0;
      this.startTime = new Date();
      console.log(this.startTime)
      gameLoseModal.classList.remove('is-active');
    });
    gameSubmitButton.addEventListener('click', (e) => {
      this.entities.doggo._removeKey();
      this.state.start('SubmitForm');
      gameLoseModal.classList.remove('is-active');
    });

    // Set the attached flag so that Phaser doesn't run this function again
    // when the game restarts
    Play.hasAttachedDOMEvents = true;
  }
  milliConvert(millis) {
    let minutes = Math.floor(millis / 60000);
    let seconds = ((millis % 60000) / 1000).toFixed(0);
    return minutes + ":" + (seconds < 10 ? '0' : '') + seconds;
  }

  /** Main draw loop -------------------------------------------------------- */
  update() {
    // Calculate game completion as a value between 0 to 1
    // This will be used by other parts of the game to make the game
    // progressively harder. Important!
    let x = this.game.width;
    this.game.progress = (-(this.entities.enemies.x.toFixed(2) / x)>=100)?100+this.game.gameSpeedUp:-(this.entities.enemies.x.toFixed(2) / x)+this.game.gameSpeedUp;
    this.game.scores = -(this.entities.enemies.x.toFixed(2) / 10) + this.scores + this.obsPoint;

    // Check for collision with enemies
    if (!this.entities.doggo.invincible) {
      this.entities.enemies.enemies.forEach(enemy => {
        this.game.physics.arcade.overlap(this.entities.doggo, enemy.enemy, this.crashEnemy, null, this);
      });
    }
    this.entities.powers.powers.forEach(power => {
      this.game.physics.arcade.overlap(this.entities.doggo, power.Powerbar, this.hitPower, null, this);
    });

    // Collide doggo with the floor
    this.game.physics.arcade.collide(this.entities.doggo, this.entities.physicsFloor);
  }

  render() {
    // const camera = this.game.camera
    // this.game.debug.camera(camera, (camera.atLimit.x || camera.atLimit.y) ? 'rgba(255,0,0,0.75)' : 'rgba(255,255,0,0.75)', false);
    // this.game.debug.cameraInfo(camera, 32, 32);
    // this.game.debug.spriteCoords(this.entities.doggo, 32, 500);

    // this.game.debug.bodyInfo(this.entities.doggo, 32, 450);
    // this.game.debug.body(this.entities.doggo);
  }

  hitPower(doggo, power) {
    // Kill the enemy so it gets removed from the world
    power.kill();

    // Hit sound effect
    this.CoinSound.play()

    //Power definition here 
    switch(power.power){
      case "SuperPow":
      this.music.pause();
      doggo.indiPower(power.power,this.music);
      if(this.game.dogType === "dog-yellow")this.game.damage = 0;
      if(this.game.dogType === "dog-orange")this.game.damage = 0,this.game.gameSpeedUp = 55;
      
      // this.game.physics.arcade.moveToPOinter(this.entities.doggo,new Phaser.Point,speed);
      break;
      case "50":
      this.scores += 50;
      break;
      case "100":
      this.scores += 100;
      break;
      case "200":
      this.scores += 200;
      break;
    }
  }

  crashEnemy(doggo, enemy) {
    // Dispatch event signal
    this.game.onDamage.dispatch();

    // Kill the enemy so it gets removed from the world
    enemy.kill();

    // Damage the doggo
    doggo.damage(this.game.damage);

    //Add up obstacle minus point.
    this.obsPoint += this.game.obsPoint;

    // Hit sound effect
    this.hitSound.play()

    // Draw smoke animation
    const smoke = new Phaser.Sprite(this.game, doggo.x, doggo.y, 'poof', 0);
    smoke.animations.add('poof');
    smoke.animations.play('poof', 24, false).onComplete.add(()=>{smoke.kill();},this)
    smoke.anchor.set(0.5, 0.75);
    this.game.world.addChild(smoke);

    // Shake the camera
    this.game.camera.shake(0.005, 250, true, Phaser.Camera.SHAKE_HORIZONTAL);
  }

  winGame() {
    if (this._hasTriggeredWinEvent) { return; }

    // Dispatch event signal
    this.game.onWin.dispatch();

    // Game Win sound effect
    this.GameWinSound.play()
    const timer = this.game.time.create(this.game, true);
    timer.add(300, () => {
      const modal = document.getElementById('game-win-modal');
      modal.classList.add('is-active');
      this.incrementCounter();
    });
    timer.start();

    this._hasTriggeredWinEvent = true;
  }
  
  loseGame() {
    // Dispatch the event signal
    this.game.onLose.dispatch();

    // Game Over sound effect
    this.GameOverSound.play()

    let gameTimeNow = new Date();
    console.log(this.startTime)
    let timeDiff = Math.abs(gameTimeNow.getTime() - this.startTime.getTime())
    this.game.gameTime = this.milliConvert(timeDiff)

    // Show the lose game modal after 500ms
    const timer = this.game.time.create(this.game, true);
    timer.add(500, () => {
      const modal = document.getElementById('game-lose-modal');
      modal.classList.add('is-active');
      this.incrementCounter();
    });
    timer.start();
  }

  incrementCounter() {
    let actualValue = null;
    document.getElementById("game-scores").innerHTML = String(this.game.scores.toFixed());
    // Animate all counters using the CountUp plugin
    const counters = Array.from(document.querySelectorAll('[data-counter]'));
    counters.forEach((counter) => {
      const value = Number(counter.innerHTML);
      const animation = new CountUp(counter, Math.round(value / 2), value, 0, 2);

      animation.start(() => {
        // Count up the scores.
        const countUp = new CountUp(counter, actualValue || value, actualValue || value, 0, 6);
        countUp.start();
      });
    });

    // Increment the counter in the backend
    // fetch('http://forefront.international/my/welcomepawsperity/counter.php?action=add')
    //   .then((response) => response.text())
    //   .then((value) => actualValue = Number(value));
  }
}

export default Play;



// WEBPACK FOOTER //
// ./js/states/play.js